import i18n from '@/i18n';
import { checkIsPublicRoute } from '@nxtpeople/nxt-vue-keycloak';
import type { AxiosError } from 'axios';
import { NavigationGuardNext, RouteLocationNormalized } from 'vue-router';
import { ContactRoleKey } from '@/core/collections/contracts-api/customers/contacts/contact.interface';
import { checkAccess, checkContactRole } from '../helpers/auth.helper';
import { isAbortedOrCanceledHttpError } from '../helpers/http-error.helper';
import { publicRoutes } from '../router';
import store from '../store';

const USER_PERMISSION_GUARD = async (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => {
   if (checkIsPublicRoute(to, publicRoutes)) return next();

   /* Checking if the user is trying to access the error page and if the error page has the correct query params. */
   if (checkErrorPage(to, next)) return;

   /* Fetching the user information from the backend. */
   try {
      await fetchUserInfo();
   } catch (error) {
      return handleUserInfoError(error, next);
   }

   /* Checking if the user has the permission to access the route. */
   return checkIfUserHasPermission(to, next);
};

function checkErrorPage(to: RouteLocationNormalized, next: NavigationGuardNext): boolean {
   const isErrorPage = to.name === 'ErrorPage';
   const hasErrorParams = to.query.error_code && to.query.error_title;
   if (!isErrorPage) return false;

   if (!hasErrorParams) {
      next('/');
      return true;
   }

   // user will not be fetched so loading can be disabled
   store.state.userInfo!.loadingUserInfo = false;
   next();
   return true;
}

function checkIfUserHasPermission(to: RouteLocationNormalized, next: NavigationGuardNext) {
   // Special handling for Documents route - check for contracts contact role
   if (to.name === 'Documents') {
      const hasContractsRole = checkContactRole(ContactRoleKey.Contracts);
      if (!hasContractsRole) {
         return next({
            path: '/error',
            query: {
               error_code: '403',
               error_title: i18n.global.t('generic.access-denied'),
               error_message: i18n.global.t('generic.u-have-no-access'),
               error_action: 'home-or-logout',
            },
         });
      }
      return next();
   }

   const requiresPermission = !!to.meta.permission;
   if (!requiresPermission) return next();

   const requiredScopes = (to.meta.scopes as Array<string>) || ['view'];
   const permission = store.state.permission?.permissions?.[to.meta.permission as string];
   const hasRequiredPermission = checkAccess(requiredScopes, permission);
   const isContactUser = store.state.userInfo?.userInfo?.is_contact_user;

   if (isContactUser && permission) {
      if (to.name === 'PerformancesOverview') return next();
      return next({ name: 'PerformancesOverview' });
   } else {
      if (hasRequiredPermission) return next();
   }

   return next({
      path: '/error',
      query: {
         error_code: '403',
         error_title: i18n.global.t('generic.access-denied'),
         error_message: i18n.global.t('generic.u-have-no-access'),
         error_action: 'home-or-logout',
      },
   });
}

let pendingUserInfoRequest: Promise<unknown> | undefined;

/**
 * Fetches whenever the user is missing, not only while loadingUserInfo is still true: a failed getUser clears
 * loadingUserInfo without a user, and later navigations must retry. Overlapping navigations share one request,
 * otherwise the cancel-token interceptor aborts the first /user-info call as a duplicate.
 */
async function fetchUserInfo() {
   if (store.state.userInfo?.userInfo) return;

   pendingUserInfoRequest ??= store.dispatch('userInfo/getUser').finally(() => {
      pendingUserInfoRequest = undefined;
   });
   await pendingUserInfoRequest;
}

function handleUserInfoError(error: unknown, next: NavigationGuardNext) {
   // The auth interceptor already redirects a 401 to the logout error page; an aborted request is superseded.
   const isUnauthorized = (error as AxiosError)?.response?.status === 401;
   if (isUnauthorized || isAbortedOrCanceledHttpError(error)) return next(false);

   // The error page is a public route, so this redirect does not fetch the user again (no redirect loop).
   return next({
      path: '/error',
      query: {
         error_code: String((error as AxiosError)?.response?.status ?? 500),
         error_title: i18n.global.t('errors.error'),
         error_message: i18n.global.t('errors.unknown-error'),
         error_action: 'refresh',
      },
   });
}

export default USER_PERMISSION_GUARD;
